/**
 * AppTabBar — 하단 5탭 (Second Pass 디자인 시스템)
 *
 * 스펙: 높이 64px, 상단 1px hair 보더, 배경 paper-1, 5등분.
 * 탭: 아이콘 20px + 11px mono 라벨, 비활성 ink-4 / 활성 pine-4.
 * 가운데 '말하기'는 클레이 마이크 원형(44×44, 인라인 — 돌출/그림자 없음).
 */

import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Compass, Mic, RotateCcw, User, type LucideIcon } from 'lucide-react';
import { useUIStore } from '@/stores/useStore';

type Tab = { to: string; icon: LucideIcon; label: { en: string; ko: string; vi: string; id: string }; match: string[]; fab?: boolean };

const TABS: Tab[] = [
  { to: '/journey', icon: Home, label: { en: 'Home', ko: '홈', vi: 'Trang chủ', id: 'Beranda' }, match: ['/journey'] },
  { to: '/explore', icon: Compass, label: { en: 'Explore', ko: '탐색', vi: 'Khám phá', id: 'Jelajah' }, match: ['/explore', '/culture-bridge'] },
  { to: '/chat', icon: Mic, label: { en: 'Speak', ko: '말하기', vi: 'Nói', id: 'Bicara' }, match: ['/chat'], fab: true },
  { to: '/review', icon: RotateCcw, label: { en: 'Review', ko: '복습', vi: 'Ôn tập', id: 'Ulas' }, match: ['/review'] },
  { to: '/profile', icon: User, label: { en: 'Profile', ko: '프로필', vi: 'Hồ sơ', id: 'Profil' }, match: ['/profile', '/settings', '/subscription', '/stats'] },
];

export default function AppTabBar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { lt } = useUIStore();

  const isActive = (match: readonly string[]) =>
    match.some((m) => pathname === m || (m !== '/journey' && pathname.startsWith(m + '/')));

  return (
    <nav
      className="relative z-40 shrink-0 border-t border-paper-4"
      style={{ background: '#fbfcfa', boxShadow: '0 -6px 16px -12px rgba(23,26,24,.16)' }}
    >
      <div className="mx-auto flex h-16 max-w-md items-stretch justify-around">
        {TABS.map((t) => {
          const active = isActive(t.match);
          const Icon = t.icon;

          if (t.fab) {
            // 가운데 말하기 = 클레이 마이크 원형 (44px 인라인, 돌출/그림자 없음)
            return (
              <button
                key={t.to}
                onClick={() => navigate(t.to)}
                className="flex flex-1 flex-col items-center justify-center gap-1"
                aria-label={lt(t.label)}
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-ds-pill bg-clay-2 text-paper-1 transition-colors active:bg-clay-3">
                  <Mic size={20} strokeWidth={2} />
                </span>
                <span className="font-mono text-[11px] uppercase tracking-wide text-ink-4">{lt(t.label)}</span>
              </button>
            );
          }

          return (
            <button
              key={t.to}
              onClick={() => navigate(t.to)}
              className="flex flex-1 flex-col items-center justify-center gap-1"
              style={{ color: active ? '#1f5d46' : '#9aa39c' }}
            >
              <Icon size={20} strokeWidth={active ? 2.2 : 1.8} />
              <span className="font-mono text-[11px] uppercase tracking-wide">{lt(t.label)}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
